import { MapInitializer } from "../MapInitializer";
import PlacesAutocomplete from "../PlacesAutocomplete";
import React, { useState } from "react";
import styled from "styled-components/macro";
import { Button, DatePicker, Input, Skeleton } from "antd";
import {
  Heart as HeartIcon,
  HeartFill as HeartFillIcon
} from "@styled-icons/bootstrap";
import { BellOutlined } from "@ant-design/icons";
import { Sliders, ChevronUp, ChevronDown } from "@styled-icons/bootstrap";
// TODO: replace date-fns with dayjs
import dayjs from "dayjs";
import { SaveSearchPreferencesModal } from "../SaveSearchPreferencesModal";
import { SizeType } from "antd/es/config-provider/SizeContext";
import { useTranslation } from "react-i18next";
import { isPositiveInteger } from "../utils";

export const Root = styled.div`
  width: 100%;
  background-color: #fff;
  border-radius: 8px;
  padding: 16px;

  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export interface IGeoSearchFiltersValue {
  location?: google.maps.LatLngLiteral;
  locationName?: string;
  price?: number;
  area?: number;
  dateFrom?: Date;
  dateTo?: Date;
  isLiked?: boolean;
  radius: number;
}

export interface IGeoSearchFiltersValidBackendValues
  extends Omit<IGeoSearchFiltersValue, "location" | "locationName"> {
  location: google.maps.LatLngLiteral;
  locationName: string;
}

export const LikeButton = styled(Button)`
  .ant-btn-icon {
    svg {
      height: 10px;
    }
  }
`;

const FiltersIcon = styled(Sliders)`
  height: 16px;
`;

const IconExpand = styled(ChevronDown)`
  height: 16px;
  margin-left: 12px;
`;

const IconCollapse = styled(ChevronUp)`
  height: 16px;
  margin-left: 12px;
`;

export const ButtonsContainer = styled.div`
  display: flex;
  gap: 16px;

  button {
    width: calc(50% - 8px);
  }
`;

const ExpandableFiltersContainer = styled.div<{ isExpanded: boolean }>`
  flex-direction: column;
  gap: 16px;

  display: ${(p) => (p.isExpanded ? "flex" : "none")}};
`;
export const ExpandedDivider = styled.div`
  border-bottom: 1px solid #d9d9d9;
`;
export const DateContainer = styled.div`
  display: flex;
  gap: 10px;
`;
export const FiltersButtonLabel = styled.span``;
export const SaveSearchButtonLabel = styled.span``;

export const GeoSearchFilters: React.FC<{
  value: IGeoSearchFiltersValue;
  onChange: (
    value: IGeoSearchFiltersValue,
    prevValue: IGeoSearchFiltersValue
  ) => void;
  onSubmit?: (value: IGeoSearchFiltersValidBackendValues) => Promise<any>;
  isSubmitting: boolean;
  isChanged: boolean;
  extraButtons?: React.ReactNode;
  isFiltersExpandedInitial?: boolean;
  inputSize?: SizeType;
  blurLocationInputOnSelect?: boolean;
  errors?: Partial<{
    [K in keyof IGeoSearchFiltersValue]: boolean;
  }>;
}> = ({
  value,
  onChange,
  onSubmit,
  isSubmitting,
  isChanged,
  extraButtons,
  isFiltersExpandedInitial,
  inputSize = "medium" as SizeType,
  blurLocationInputOnSelect = false,
  errors
}) => {
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(
    isFiltersExpandedInitial ?? false
  );
  const [showSaveModal, setShowSaveModal] = useState(false);
  const { t } = useTranslation("geomap");

  return (
    <MapInitializer>
      <Root>
        <PlacesAutocomplete
          size={inputSize}
          displayValue={
            value.location && value.locationName
              ? {
                  location: value.location,
                  displayName: value.locationName
                }
              : undefined
          }
          blurOnSelect={blurLocationInputOnSelect}
          onChange={(coords, data) => {
            onChange(
              {
                ...value,
                location: coords,
                locationName: data?.name
              },
              value
            );
          }}
          error={errors?.["location"] || errors?.["locationName"]}
        />
        <ExpandableFiltersContainer isExpanded={isFiltersExpanded}>
          <div
            style={{
              display: "flex",
              gap: 16
            }}
          >
            <Input
              suffix="€"
              placeholder={t("geoSearchFilters.maxPrice")}
              min={0}
              value={value.price ?? ""}
              allowClear
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              size={inputSize}
              onChange={(e) => {
                if (e.target.value === "") {
                  onChange(
                    {
                      ...value,
                      price: undefined
                    },
                    value
                  );
                  return;
                }
                if (!isPositiveInteger(e.target.value)) {
                  return;
                }
                const parsedValue = Number(e.target.value);
                onChange(
                  {
                    ...value,
                    price: parsedValue
                  },
                  value
                );
              }}
              status={errors?.["price"] ? "error" : ""}
            />
            <Input
              suffix="m2"
              placeholder={t("geoSearchFilters.minSize")}
              min={0}
              value={value.area ?? ""}
              allowClear
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              size={inputSize}
              onChange={(e) => {
                if (e.target.value === "") {
                  onChange(
                    {
                      ...value,
                      area: undefined
                    },
                    value
                  );
                  return;
                }

                if (!isPositiveInteger(e.target.value)) {
                  return;
                }
                const parsedValue = Number(e.target.value);
                onChange(
                  {
                    ...value,
                    area: parsedValue
                  },
                  value
                );
              }}
              status={errors?.["area"] ? "error" : ""}
            />
          </div>
          <LikeButton
            type={value.isLiked ? "primary" : "default"}
            icon={value.isLiked ? <HeartFillIcon /> : <HeartIcon />}
            size={inputSize}
            onClick={() => {
              onChange(
                {
                  ...value,
                  isLiked: !value.isLiked
                },
                value
              );
            }}
          >
            {t("geoSearchFilters.favourites")}
          </LikeButton>
          <DateContainer>
            <DatePicker
              allowClear={false}
              size={inputSize}
              placeholder={t("geoSearchFilters.startDate")}
              value={value.dateFrom ? dayjs(value.dateFrom) : null}
              // onOpenChange={setIsDatePickerOpen}
              onChange={(_date) => {
                const date = _date?.toDate();
                onChange(
                  {
                    ...value,
                    dateFrom: date,
                    dateTo:
                      date &&
                      value.dateTo &&
                      date.getTime() > value.dateTo.getTime()
                        ? undefined
                        : value.dateTo
                  },
                  value
                );
              }}
              status={errors?.["dateFrom"] ? "error" : ""}
            />

            <DatePicker
              allowClear
              size={inputSize}
              placeholder={t("geoSearchFilters.endDate")}
              value={value.dateTo ? dayjs(value.dateTo) : null}
              // onOpenChange={setIsDatePickerOpen}
              disabledDate={(date) => {
                if (!value.dateFrom) {
                  return false;
                }
                return date.toDate().getTime() < value.dateFrom?.getTime();
              }}
              onChange={(date) => {
                onChange(
                  {
                    ...value,
                    dateTo: date?.toDate()
                  },
                  value
                );
              }}
              status={errors?.["dateTo"] ? "error" : ""}
            />
          </DateContainer>
        </ExpandableFiltersContainer>
        {isFiltersExpanded ? <ExpandedDivider /> : null}
        <ButtonsContainer>
          <Button
            type={"default"}
            size={inputSize}
            onClick={() => {
              setIsFiltersExpanded((prev) => !prev);
            }}
            icon={<FiltersIcon />}
          >
            <FiltersButtonLabel>
              {t("geoSearchFilters.filters")}{" "}
            </FiltersButtonLabel>
            {isFiltersExpanded ? <IconCollapse /> : <IconExpand />}
          </Button>
          <Button
            type={isChanged ? "primary" : "default"}
            size={inputSize}
            loading={isSubmitting}
            onClick={() => {
              setShowSaveModal(true);
            }}
            icon={<BellOutlined />}
          >
            <SaveSearchButtonLabel>
              {t("geoSearchFilters.saveSearch")}{" "}
            </SaveSearchButtonLabel>
          </Button>
          {extraButtons}
        </ButtonsContainer>
      </Root>
      {showSaveModal ? (
        <SaveSearchPreferencesModal
          isShow={true}
          inputSize={inputSize}
          preferences={value}
          onSubmit={(value) => {
            onSubmit?.(value).finally(() => {
              setShowSaveModal(false);
            });
          }}
          isLoading={isSubmitting}
          onClose={() => {
            setShowSaveModal(false);
          }}
        />
      ) : null}
    </MapInitializer>
  );
};

export const GeoSearchFiltersSkeleton = () => {
  return (
    <Root>
      <Skeleton active />
    </Root>
  );
};
